import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  request,
  type MetaResponse,
  type SubmissionList,
  type SubmissionDetail,
  type OverviewResponse,
  type AdminsResponse,
  type PermissionMatrix,
  type AdminPermissions,
  type NewsletterResponse,
  type SubscriberList,
  type AnalyticsResponse,
  type SendProgress,
  type NewsletterPreview,
  type NewsletterTestResult,
  type InviteResult,
  type PeopleResponse,
  type Person,
  type AdminRow,
  type PhiMatch,
} from './client';

export type { InviteResult, Person };

/**
 * `/meta` is the workflow contract — statuses, stages, routing, studies.
 * Fetched once and held: it changes only on an API deploy, and every section
 * reads from it rather than hardcoding a status list.
 */
export function useMeta() {
  return useQuery({
    queryKey: ['meta'],
    queryFn: () => request<MetaResponse>('/meta/'),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export interface SubmissionFilters {
  tab: string;
  status: string;
  q: string;
  sort: 'newest' | 'oldest' | 'name';
  page: number;
  pageSize: number;
  /** One study's rows, or `none` for rows that carry no study. */
  study?: string;
  /** Comma-separated status keys, matched whatever the tab. */
  statuses?: string;
}

export function useSubmissions(filters: SubmissionFilters) {
  return useQuery({
    queryKey: ['submissions', filters],
    queryFn: () =>
      request<SubmissionList>('/submissions/', {
        query: { ...filters },
      }),
    // Keeps the table on screen while a filter change is in flight instead of
    // collapsing to a spinner on every keystroke.
    placeholderData: (prev) => prev,
  });
}

export function useSubmission(id: string | null) {
  return useQuery({
    queryKey: ['submission', id],
    queryFn: () => request<SubmissionDetail>(`/submissions/${id}`),
    enabled: id !== null,
  });
}

/**
 * Set the study a submission belongs to — for a row that arrived with none,
 * or a waitlist sign-up that named several. The payload is left as sent;
 * only the column the boards and the scope read moves.
 */
export function useSetStudy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, study }: { id: string; study: string }) =>
      request(`/submissions/${id}/study`, { method: 'PATCH', body: { study } }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['submission', id] });
    },
  });
}

/** `enabled` lets a caller without `submissions:read` skip the request instead of collecting a 403. */
export function useOverview(enabled = true) {
  return useQuery({
    queryKey: ['overview'],
    queryFn: () => request<OverviewResponse>('/submissions/overview'),
    enabled,
  });
}

/** `enabled` lets a caller without `admins:read` skip the request instead of collecting a 403. */
export function useAdmins(enabled = true) {
  return useQuery({
    queryKey: ['admins'],
    queryFn: () => request<AdminsResponse>('/admins/'),
    enabled,
  });
}

/**
 * Active staff, for every owner picker (records, contacts, tickets). Any
 * signed-in account may read it, so a picker never depends on admins:read or
 * meetings:read the way the old sources did.
 */
export function usePeople(enabled = true) {
  return useQuery({
    queryKey: ['people'],
    queryFn: () => request<PeopleResponse>('/admins/people'),
    enabled,
    staleTime: 60_000,
  });
}

/** `enabled`: false for a role without `newsletter:read` — the Dashboard's Subscribers card. */
export function useNewsletter(enabled = true) {
  return useQuery({
    queryKey: ['newsletter'],
    queryFn: () => request<NewsletterResponse>('/newsletter/'),
    enabled,
  });
}

/** `enabled`: false for a role without `analytics:read` — the Dashboard's visit cards. */
export function useAnalytics(enabled = true) {
  return useQuery({
    queryKey: ['analytics'],
    queryFn: () => request<AnalyticsResponse>('/analytics/'),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      request(`/submissions/${id}/status`, { method: 'PATCH', body: { status } }),
    onSuccess: (_data, { id, status }) => {
      // A status move changes the row, the filter tiles and the Overview counts
      // — and, for someone endorsed here, their card on the sending board.
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['submission', id] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      void qc.invalidateQueries({ queryKey: ['endorsed-out'] });
      // Not a match opens the "Send the not-a-match letter." clock (API, 20 Oct 2026).
      if (status === 'not-a-match') void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

/**
 * An invite emails a temporary password (API 049). `temporaryPassword` comes
 * back only when that email did not go, for the inviter to hand over.
 */
export function useInviteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; email: string; role: string }) =>
      request<InviteResult>('/admins/', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admins'] });
      // A new holder changes a role's member count.
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/** A new temporary password for someone who has not signed in yet; the old one stops working. */
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<InviteResult>(`/admins/${id}/invite`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

/** What each capability allows — the reference the Admin access panel shows. */
export function usePermissionMatrix() {
  return useQuery({
    queryKey: ['permission-matrix'],
    queryFn: () => request<PermissionMatrix>('/admins/permissions/'),
  });
}

/** The ticks stored against one account, whatever capability it currently holds. */
export function useAdminPermissions(id: string | null) {
  return useQuery({
    queryKey: ['admin-permissions', id],
    queryFn: () => request<AdminPermissions>(`/admins/permissions/${id}`),
    enabled: id !== null,
  });
}

/** Replace one account's ticks. */
export function useSaveAdminPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      request(`/admins/permissions/${id}`, { method: 'PUT', body: { permissions } }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['admin-permissions', v.id] });
      void qc.invalidateQueries({ queryKey: ['admins'] });
    },
  });
}

export function useChangeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      request(`/admins/${id}/role`, { method: 'PATCH', body: { role } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admins'] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/**
 * `DELETE /admins/{id}` suspends: the row stays so the record of what the
 * person did survives (a clinical act refuses deletion outright), their
 * sessions are revoked, and they cannot sign back in.
 */
export function useSuspendAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request(`/admins/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admins'] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
      // A suspended account still shows on the studies it was given, marked.
      void qc.invalidateQueries({ queryKey: ['study-members'] });
    },
  });
}

/**
 * Reinstate a suspended account (POST /admins/{id}/reactivate, QA C-ADM-019).
 *
 * They come back with the role, boxes and studies they had — as active, or as
 * invited when they never chose a password, and then they need a new invite.
 * The answer is only `{ ok }`, so the list is read again and the person's own
 * row says which; null when that read failed. 403 and 409 carry the API's
 * sentence ("Only an owner can change an owner's account.", "This account is
 * not suspended.").
 */
export function useReactivateAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AdminRow['status'] | null> => {
      await request<{ ok: boolean }>(`/admins/${id}/reactivate`, { method: 'POST' });
      try {
        const list = await qc.fetchQuery({
          queryKey: ['admins'],
          queryFn: () => request<AdminsResponse>('/admins/'),
          staleTime: 0,
        });
        return list.admins.find((a) => a.id === id)?.status ?? null;
      } catch {
        return null;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admins'] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
      void qc.invalidateQueries({ queryKey: ['study-members'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Set someone's PHI-Match level (PATCH /admins/{id}/phi-match, admins:manage,
 * QA C-ADM-035). The body is `{ phiMatch }`. Refused with a sentence: on your
 * own account (403), on an owner's unless you are one (403), above your own
 * level unless you are an owner (403), and anything but None on a Developer
 * (422).
 */
export function useSetPhiMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, phiMatch }: { id: string; phiMatch: PhiMatch }) =>
      request<{ ok: boolean; phiMatch: PhiMatch; phiMatchLabel: string }>(`/admins/${id}/phi-match`, {
        method: 'PATCH',
        body: { phiMatch },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

/**
 * Who a newsletter goes to: everyone on both lists, the people who asked for
 * the letter from the lab, or the subscribers of the named studies. Omitted,
 * the API sends to everyone, which is what it did before it had audiences.
 */
export type NewsletterAudience = 'everyone' | 'letter' | 'studies';
export interface SendNewsletterBody {
  subject: string;
  body: string;
  audience?: NewsletterAudience;
  studies?: string[];
}

export function useSendNewsletter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendNewsletterBody) =>
      request<{ id: string; recipientCount: number }>('/newsletter/send', {
        method: 'POST',
        body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['newsletter'] }),
  });
}

/**
 * How many people the subscribers of several studies are. A union, not a sum
 * — one person can follow two studies — so only the API can say. Asked only
 * when two or more studies are ticked; one study's count is on the summary.
 */
export function useRecipientCount(slugs: string[]) {
  const key = [...slugs].sort();
  return useQuery({
    queryKey: ['newsletter', 'recipients', key],
    queryFn: () =>
      request<{ count: number }>(
        `/newsletter/recipients?audience=studies&studies=${encodeURIComponent(key.join(','))}`,
      ),
    enabled: key.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/** A letter: the subject and the text as written on the composer. */
export interface LetterDraft {
  subject: string;
  body: string;
}

/**
 * A draft rendered by the API exactly as a reader gets it: the branded layout,
 * the footer, the leave-the-list link, `{first name}` filled with yours. Keyed
 * on the draft, so the page's pause-after-typing re-renders it and returning
 * to an earlier draft is instant. Null asks for nothing.
 */
export function useLetterPreview(draft: LetterDraft | null) {
  return useQuery({
    queryKey: ['newsletter-preview', draft],
    queryFn: () => request<NewsletterPreview>('/newsletter/preview', { method: 'POST', body: draft! }),
    enabled: draft !== null,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
}

/** One copy of a draft to the signed-in person, subject marked "[Test]". */
export function useSendTestLetter() {
  return useMutation({
    mutationFn: (draft: LetterDraft) =>
      request<NewsletterTestResult>('/newsletter/test', { method: 'POST', body: draft }),
  });
}

/** Poll a send until it finishes — the API queues rather than sending inline. */
export function useSendProgress(id: string | null) {
  return useQuery({
    queryKey: ['send', id],
    queryFn: () => request<SendProgress>(`/newsletter/sends/${id}`),
    enabled: id !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'sent' || status === 'failed' ? false : 1500;
    },
  });
}

/** Whose rows the subscriber list shows: still on a list, gone, or both. The API's `status`. */
export type SubscriberStatus = 'active' | 'unsubscribed' | 'all';
export interface SubscriberFilters {
  /** Part of an address; the API searches email only. */
  q: string;
  status: SubscriberStatus;
  page: number;
  pageSize: number;
}

/**
 * Every subscriber, a page at a time, newest first (GET /subscribers/,
 * newsletter:read). Not PHI: the list is marketing data in its own table.
 */
export function useSubscribers(filters: SubscriberFilters) {
  return useQuery({
    queryKey: ['subscribers', filters],
    queryFn: () => request<SubscriberList>('/subscribers/', { query: { ...filters } }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Take someone off every list by hand (DELETE /subscribers/{id},
 * newsletter:send). The row stays, marked as left; signing up again on the
 * site puts them back, exactly as their own leave-the-list link does.
 */
export function useUnsubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: boolean }>(`/subscribers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscribers'] });
      // The counts on the tile and on each audience choice.
      void qc.invalidateQueries({ queryKey: ['newsletter'] });
    },
  });
}
